import styled from '@emotion/styled';
import { css } from '@emotion/core';

const responsiveCSS = ({ theme, noPadding, isDoubleBanner }) => css`
  margin-left: auto;
  margin-right: auto;
  padding-left: ${noPadding || isDoubleBanner ? 0 : '3rem'};
  padding-right: ${noPadding || isDoubleBanner ? 0 : '3rem'};

  @media screen and (min-width: ${theme.breakpoint.medium}) {
    padding-left: ${noPadding ? 0 : '6.5vw'};
    padding-right: ${noPadding ? 0 : '6.5vw'};
  }
`;

const Page = styled.main`
  position: relative;
  min-height: 100vh;

  ${({ background, theme }) => !background && css`
    --background-color: ${theme.color.white};
  `}

  ${({ background, theme }) => background === 'default' && css`
    --background-color: ${theme.color.background};
    background-image: url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-bg-right.jpg);
    background-position: top right;
    background-color: ${theme.color.background};
    background-repeat: repeat-y;

    @media screen and (min-width: ${theme.breakpoint.small}) {
      background-image: url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-bg-left.jpg),
                        url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-bg-right.jpg);
      background-position: top left, top right;
      background-color: ${theme.color.background};
      background-repeat: repeat-y;
    }
  `}

  ${({ background, theme }) => background === 'white' && css`
    --background-color: ${theme.color.white};
    background-color: ${theme.color.white};
  `}

  ${({ background, theme }) => background === 'cream' && css`
    --background-color: ${theme.color.background};
    background-color: ${theme.color.background};
  `}

  ${({ background, theme }) => background === 'holiday' && css`
    --background-color: ${theme.color.background};
    background-image: url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-holiday-bg-right.jpg);
    background-position: top right;
    background-color: ${theme.color.background};
    background-repeat: repeat-y;

    @media screen and (min-width: ${theme.breakpoint.small}) {
      background-image: url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-holiday-bg-left.jpg),
                        url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-holiday-bg-right.jpg);
      background-position: top left, top right;
      background-color: ${theme.color.background};
      background-repeat: repeat-y;
    }
  `}

  ${({ background }) => background === 'opaque' && css`
    --background-color: hsla(120, 100%, 100%, 0.7);
    background-color: hsla(120, 100%, 100%, 0.7);
  `}

  ${({ background }) => background === 'dark-opaque' && css`
    --background-color: hsla(0, 0%, 0%, 0.7);
    background-color: hsla(0, 0%, 0%, 0.7);
  `}

  ${({ background }) => background === 'transparent' && css`
    --background-color: transparent;
    background-color: transparent;
  `}
`;

const Content = styled.div`
  ${responsiveCSS};
  position: relative;
  padding-bottom: ${({ noPadding }) => (noPadding ? 0 : '2rem')};
  padding-top: ${({ noPadding }) => (noPadding ? 0 : '2rem')};

  ${({ isAccordion, theme }) => isAccordion && css`
    & ~ & {
      padding-top: 0;

      dt {
        border-top: none;
      }

      dd {
        border-bottom: 1px solid ${theme.color.brand};
      }
    }

    div:not(&) + & dt {
      border-top: 1px solid ${theme.color.brand};
    }

    :not(:last-of-type) {
      padding-bottom: 0;
    }
  `};

  ${({ theme, noPadding }) => css`
    @media screen and (min-width: ${theme.breakpoint.medium}) {
      padding-bottom: ${noPadding ? 0 : '3rem'};
      padding-top: ${noPadding ? 0 : '3rem'};
    }
  `}

  ${({ isWhite, theme }) => isWhite && css`
    background: ${theme.color.white};
  `}

  ${({ isCircleBanner, theme, noPadding }) => isCircleBanner && css`
    @media screen and (min-width: ${theme.breakpoint.medium}) {
      width: 33.333%;
      margin: auto;
      padding: ${noPadding ? 0 : '3rem 4.5vw'};
      display: inline-block;
    }
  `}

  ${({ isHeading }) => isHeading && css`
    h1 {
      text-align: left;
    }

    hr {
      background: ${({ theme }) => theme.color.brand};
    }
  `}

  ${({ isSocialShare }) => isSocialShare && css`
    padding-top: 0 !important;
  `}

  ${({ isFullWidthBelowMedium, theme }) => isFullWidthBelowMedium && css`
    padding: 0;

    @media screen and (min-width: ${theme.breakpoint.medium}) {
      padding: ${theme.modularScale['2xlarge']} 6.5vw 0;
    }
  `}

  ${({ isFullWidthMobileOnly }) => isFullWidthMobileOnly && css`
    padding-left: 0;
    padding-right: 0;
  `}

  ${({ layout }) => layout === 'small' && css`
    && { max-width: 50rem }
  `}

  ${({ layout }) => layout === 'medium' && css`
    && { max-width: 80rem }
  `}

  ${({ layout }) => layout === 'large' && css`
    && { max-width: 120rem }
  `}
`;

const PageWithNav = styled.div`
  display: grid;
  grid-template-columns: minmax(28%, 1fr) minmax(72%, 3fr);
  padding: 3rem 6.5vw;

  > ${Content} {
    grid-column: 1 / span 2;
    width: 100%;
    padding: 0;

    h1 {
      padding-bottom: 3rem;
      text-align: center;
    }
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    > ${Content} {
      grid-column: 2 / span 1;

      h1 {
        padding-bottom: 5rem;
        text-align: left;
      }
    }
  }

  ${Content}:last-of-type {
    margin-bottom: 5rem;
  }
`;

Page.whyDidYouRender = true;
Content.whyDidYouRender = true;
PageWithNav.whyDidYouRender = true;

export {
  Content,
  Page,
  PageWithNav,
  responsiveCSS
};
